import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRightLeft, History, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { useBalance } from '../contexts/BalanceContext';
import CryptoIcon from '../components/CryptoIcon';
import {
  CURRENCIES,
  fetchBalance as fetchBalanceService,
  fetchConversionRate as fetchRateService,
  fetchConversionHistory as fetchHistoryService,
  performConversion as performConversionService,
  formatDate,
  formatAmount
} from '../services/conversionService';

const Transfer = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { refreshBalance } = useBalance();
  const [activeTab, setActiveTab] = useState('convert'); // 'convert' or 'history'
  const [fromAsset, setFromAsset] = useState('USDT');
  const [toAsset, setToAsset] = useState('BTC');
  const [amount, setAmount] = useState('');
  const [balance, setBalance] = useState(null);
  const [conversionRate, setConversionRate] = useState(null);
  const [convertedAmount, setConvertedAmount] = useState(0);
  const [conversionHistory, setConversionHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const data = await fetchBalanceService();
        setBalance(data);
      } catch (error) {
        console.error('Error fetching balance:', error);
      }
    };
    fetchBalance();
    
    // Fetch conversion history if on history tab
    if (activeTab === 'history') {
      fetchConversionHistory();
    }
  }, [activeTab]);

  // Fetch conversion rate when assets change
  useEffect(() => {
    if (fromAsset && toAsset && fromAsset !== toAsset) {
      fetchConversionRate();
    }
  }, [fromAsset, toAsset]);

  // Calculate converted amount when rate or amount changes
  useEffect(() => {
    if (conversionRate && amount && !isNaN(amount) && parseFloat(amount) > 0) {
      const converted = parseFloat(amount) * conversionRate.rate;
      setConvertedAmount(converted);
    } else {
      setConvertedAmount(0);
    }
  }, [conversionRate, amount]);

  const fetchConversionRate = async () => {
    try {
      const data = await fetchRateService(fromAsset, toAsset);
      setConversionRate(data);
    } catch (error) {
      console.error('Error fetching conversion rate:', error);
    }
  };

  const fetchConversionHistory = async () => {
    try {
      const conversions = await fetchHistoryService(20);
      setConversionHistory(conversions);
    } catch (error) {
      console.error('Error fetching conversion history:', error);
    }
  };

  const handleConvert = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast({ title: 'Error', description: 'Please enter a valid amount', variant: 'destructive' });
      return;
    }

    if (fromAsset === toAsset) {
      toast({ title: 'Error', description: 'Cannot convert to same asset', variant: 'destructive' });
      return;
    }

    if (balance?.balances && balance.balances[fromAsset] < parseFloat(amount)) {
      toast({ title: 'Error', description: `Insufficient ${fromAsset} balance`, variant: 'destructive' });
      return;
    }

    setIsLoading(true);
    
    try {
      const data = await performConversionService(fromAsset, toAsset, amount);
      
      toast({
        title: '✅ Conversion Complete',
        description: `Successfully converted ${amount} ${fromAsset} to ${formatAmount(data.to_amount, toAsset)} ${toAsset}`,
      });

      // Refresh balance
      const updatedBalance = await fetchBalanceService();
      setBalance(updatedBalance);
      await refreshBalance(); // Refresh global balance context
      
      // Switch to history tab to show the conversion
      setAmount('');
      setActiveTab('history');
    } catch (error) {
      console.error('Error converting:', error);
      toast({ 
        title: 'Conversion Failed', 
        description: error.message || 'Failed to process conversion', 
        variant: 'destructive' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const swapAssets = () => {
    const temp = fromAsset;
    setFromAsset(toAsset);
    setToAsset(temp);
  };

  // Use shared CURRENCIES from conversionService (now uses {symbol, name} format)
  const assets = CURRENCIES.map(c => ({ id: c.symbol, name: c.name }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <div className="sticky top-0 bg-slate-900/95 backdrop-blur-sm border-b border-cyan-500/20 z-10">
        <div className="flex items-center justify-between px-4 py-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold">Back</span>
          </button>
          <h1 className="text-lg font-bold text-cyan-400">Coin Conversion</h1>
          <div className="w-16"></div>
        </div>

        {/* Tabs */}
        <div className="flex border-t border-cyan-500/20">
          <button
            onClick={() => setActiveTab('convert')}
            className={`flex-1 py-3 px-4 font-semibold transition-colors ${
              activeTab === 'convert'
                ? 'text-cyan-400 border-b-2 border-cyan-400'
                : 'text-cyan-600 hover:text-cyan-400'
            }`}
          >
            <ArrowRightLeft className="w-4 h-4 inline mr-2" />
            Convert
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 py-3 px-4 font-semibold transition-colors ${
              activeTab === 'history'
                ? 'text-cyan-400 border-b-2 border-cyan-400'
                : 'text-cyan-600 hover:text-cyan-400'
            }`}
          >
            <History className="w-4 h-4 inline mr-2" />
            History
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6">
        {activeTab === 'convert' ? (
          <>
            {/* From Asset */}
            <div className="mb-4">
              <Label className="text-cyan-400 mb-2 block">From</Label>
              <div className="bg-slate-800/50 border border-cyan-500/30 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {fromAsset !== 'GOLD' && <CryptoIcon symbol={fromAsset} size={24} />}
                    <select
                      value={fromAsset}
                      onChange={(e) => setFromAsset(e.target.value)}
                      className="bg-transparent text-cyan-400 text-lg font-semibold outline-none"
                    >
                      {assets.map(asset => (
                        <option key={asset.id} value={asset.id}>
                          {asset.id} - {asset.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-cyan-600">Balance</div>
                    <div className="text-sm text-cyan-400 font-semibold">
                      {balance?.balances ? balance.balances[fromAsset]?.toLocaleString() || '0' : '0'}
                    </div>
                  </div>
                </div>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="h-12 bg-transparent border-0 text-2xl font-bold text-cyan-100 placeholder:text-cyan-800 focus:ring-0 px-0"
                />
                {balance?.balances && balance.balances[fromAsset] > 0 && (
                  <button
                    onClick={() => setAmount(balance.balances[fromAsset]?.toString() || '0')}
                    className="text-xs text-cyan-500 hover:text-cyan-400 mt-2"
                  >
                    Max: {balance.balances[fromAsset]?.toLocaleString() || '0'}
                  </button>
                )}
              </div>
            </div>

            {/* Conversion Rate */}
            {conversionRate && (
              <div className="mb-4 px-4">
                <div className="flex items-center justify-center gap-2 text-sm text-cyan-400">
                  <TrendingUp className="w-4 h-4" />
                  <span>1 {fromAsset} = {conversionRate.rate.toFixed(8)} {toAsset}</span>
                </div>
                <div className="text-center text-xs text-cyan-600 mt-1">
                  ${conversionRate.from_price_usd} → ${conversionRate.to_price_usd}
                </div>
              </div>
            )}

            {/* Swap Button */}
            <div className="flex justify-center -my-2 relative z-10">
              <button
                onClick={swapAssets}
                className="w-12 h-12 rounded-full bg-cyan-500 hover:bg-cyan-400 flex items-center justify-center neon-icon transition-all"
              >
                <ArrowRightLeft className="w-6 h-6 text-white" />
              </button>
            </div>

            {/* To Asset */}
            <div className="mb-6">
              <Label className="text-cyan-400 mb-2 block">To</Label>
              <div className="bg-slate-800/50 border border-cyan-500/30 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {toAsset !== 'GOLD' && <CryptoIcon symbol={toAsset} size={24} />}
                    <select
                      value={toAsset}
                      onChange={(e) => setToAsset(e.target.value)}
                      className="bg-transparent text-cyan-400 text-lg font-semibold outline-none"
                    >
                      {assets.map(asset => (
                        <option key={asset.id} value={asset.id}>
                          {asset.id} - {asset.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-cyan-600">Balance</div>
                    <div className="text-sm text-cyan-400 font-semibold">
                      {balance?.balances ? balance.balances[toAsset]?.toLocaleString() || '0' : '0'}
                    </div>
                  </div>
                </div>
                <div className="h-12 flex items-center text-2xl font-bold text-cyan-400">
                  ≈ {convertedAmount.toFixed(8)}
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-6">
              <div className="flex gap-2">
                <span className="text-blue-400 text-xl">ℹ️</span>
                <div className="text-sm text-cyan-600">
                  <p>Convert your holdings between different assets using real-time market rates from Tatum API with CoinGecko fallback.</p>
                  <p className="mt-1">No fees applied. Instant conversion.</p>
                </div>
              </div>
            </div>

            {/* Convert Button */}
            <Button
              onClick={handleConvert}
              disabled={isLoading || !amount || parseFloat(amount) <= 0}
              className="w-full h-14 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold rounded-full neon-button text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowRightLeft className="w-5 h-5 mr-2" />
              {isLoading ? 'Converting...' : 'Convert Now'}
            </Button>
          </>
        ) : (
          <>
            {/* Conversion History */}
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-cyan-400 font-bold text-lg">Conversion History</h2>
                <span className="text-cyan-600 text-sm">{conversionHistory.length} conversions</span>
              </div>

              {conversionHistory.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-16 h-16 mx-auto text-cyan-800 mb-4" />
                  <p className="text-cyan-600">No conversion history yet</p>
                  <button
                    onClick={() => setActiveTab('convert')}
                    className="mt-4 text-cyan-400 hover:text-cyan-300 font-semibold"
                  >
                    Make your first conversion →
                  </button>
                </div>
              ) : (
                conversionHistory.map((conversion, index) => (
                  <div
                    key={conversion.conversion_id || index}
                    className="bg-slate-800/50 border border-cyan-500/30 rounded-xl p-4"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          {conversion.from_currency !== 'GOLD' && (
                            <CryptoIcon symbol={conversion.from_currency} size={24} />
                          )}
                          <span className="text-cyan-400 font-semibold">
                            {conversion.from_currency}
                          </span>
                        </div>
                        <ArrowRightLeft className="w-4 h-4 text-cyan-600" />
                        <div className="flex items-center gap-2">
                          {conversion.to_currency !== 'GOLD' && (
                            <CryptoIcon symbol={conversion.to_currency} size={24} />
                          )}
                          <span className="text-cyan-400 font-semibold">
                            {conversion.to_currency}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-cyan-600">
                          {formatDate(conversion.timestamp)}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-cyan-600 text-xs mb-1">From Amount</div>
                        <div className="text-cyan-400 font-semibold">
                          {conversion.from_amount.toLocaleString()} {conversion.from_currency}
                        </div>
                      </div>
                      <div>
                        <div className="text-cyan-600 text-xs mb-1">To Amount</div>
                        <div className="text-green-400 font-semibold">
                          {conversion.to_amount.toFixed(8)} {conversion.to_currency}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-cyan-600 text-xs mb-1">Rate</div>
                        <div className="text-cyan-400 font-semibold">
                          1 {conversion.from_currency} = {conversion.rate.toFixed(8)} {conversion.to_currency}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Transfer;